import { Link as AmplifyLink } from '@aws-amplify/ui-react';
import { useState } from 'react';
import { BiChevronDown, BiChevronUp } from 'react-icons/bi';
import { Link } from 'react-router-dom';
import { FieldName, SortOptions } from '../../utils/CustomTypes';
import General from '../../utils/GeneralUtils';
import { TableFields } from '../../utils/TableFields';
import Currency from '../Currency';
import GradeSpan from '../GradeSpan';
import MetricValue from '../MetricValue';
import NA from '../NA';
import SortLabel from '../SortLabel';
import UniversalTag from '../UniversalTag';

// Institution types supported by this component
// DISTRICT, SCHOOL, AGENCY

/**
 * Unified table row header for all institution types
 * Shows appropriate columns based on the tableFields configuration
 */
export function TableRowHeaderInstitution({
  isAllSortingDisabled = false,
  showSubheader,
  currentSort,
  tableFields,
  onSort
}: {
  isAllSortingDisabled?: boolean,
  showSubheader: boolean,
  currentSort: SortOptions,
  tableFields: TableFields,
  onSort: (sortOptions: SortOptions) => void
}) {
  if (!showSubheader) return <></>

  return <>
    <div className='results-subheader' style={{ justifyContent: 'flex-end', paddingLeft: 45 }}>
      {
        tableFields.isFieldVisible(FieldName.NAME) &&
          <div style={tableFields.getStyle(FieldName.NAME)}>
            <SortLabel
              fieldName={FieldName.NAME}
              isDisabled={isAllSortingDisabled ? true : !tableFields.isFieldSortable(FieldName.NAME)}
              currentSort={currentSort}
              onSort={onSort}
            />
          </div>
      }
      {/* Institution type column */}
      {
        tableFields.isFieldVisible(FieldName.TYPE) &&
          <div style={tableFields.getStyle(FieldName.TYPE)}>
            <SortLabel
              fieldName={FieldName.TYPE}
              isDisabled={isAllSortingDisabled ? true : !tableFields.isFieldSortable(FieldName.TYPE)}
              currentSort={currentSort}
              onSort={onSort}
            />
          </div>
      }
      {/* Subtype column */}
      {
        tableFields.isFieldVisible(FieldName.SUBTYPE) &&
          <div style={tableFields.getStyle(FieldName.SUBTYPE)}>
            <SortLabel
              fieldName={FieldName.SUBTYPE}
              isDisabled={isAllSortingDisabled ? true : !tableFields.isFieldSortable(FieldName.SUBTYPE)}
              currentSort={currentSort}
              onSort={onSort}
            />
          </div>
      }
      {/* Access column */}
      {
        tableFields.isFieldVisible(FieldName.ACCESS) &&
          <div style={tableFields.getStyle(FieldName.ACCESS)}>
            <SortLabel
              fieldName={FieldName.ACCESS}
              isDisabled={isAllSortingDisabled ? true : !tableFields.isFieldSortable(FieldName.ACCESS)}
              currentSort={currentSort}
              onSort={onSort}
            />
          </div>
      }
      {/* School-specific: Title One */}
      {
        tableFields.isFieldVisible(FieldName.TITLE_ONE) &&
          <div style={tableFields.getStyle(FieldName.TITLE_ONE)}>
            <SortLabel
              fieldName={FieldName.TITLE_ONE}
              isDisabled={isAllSortingDisabled ? true : !tableFields.isFieldSortable(FieldName.TITLE_ONE)}
              currentSort={currentSort}
              onSort={onSort}
            />
          </div>
      }
      {/* School-specific: Grade Span */}
      {
        tableFields.isFieldVisible(FieldName.GRADE_SPAN) &&
          <div style={tableFields.getStyle(FieldName.GRADE_SPAN)}>
            <SortLabel
              fieldName={FieldName.GRADE_SPAN}
              isDisabled={isAllSortingDisabled ? true : !tableFields.isFieldSortable(FieldName.GRADE_SPAN)}
              currentSort={currentSort}
              onSort={onSort}
            />
          </div>
      }
      {/* Agency-specific: State */}
      {
        tableFields.isFieldVisible(FieldName.LOC_STATE) &&
          <div style={tableFields.getStyle(FieldName.LOC_STATE)}>
            <SortLabel
              fieldName={FieldName.LOC_STATE}
              isDisabled={isAllSortingDisabled ? true : !tableFields.isFieldSortable(FieldName.LOC_STATE)}
              currentSort={currentSort}
              onSort={onSort}
            />
          </div>
      }
      {/* Agency-specific: Total Districts */}
      {
        tableFields.isFieldVisible(FieldName.TOTAL_DISTRICTS) &&
          <div style={tableFields.getStyle(FieldName.TOTAL_DISTRICTS)}>
            <SortLabel
              fieldName={FieldName.TOTAL_DISTRICTS}
              isDisabled={isAllSortingDisabled ? true : !tableFields.isFieldSortable(FieldName.TOTAL_DISTRICTS)}
              currentSort={currentSort}
              onSort={onSort}
            />
          </div>
      }
      {/* District-specific: Revenue */}
      {
        tableFields.isFieldVisible(FieldName.FSCL_REV_TOTAL) &&
          <div style={tableFields.getStyle(FieldName.FSCL_REV_TOTAL)}>
            <SortLabel
              fieldName={FieldName.FSCL_REV_TOTAL}
              isDisabled={isAllSortingDisabled ? true : !tableFields.isFieldSortable(FieldName.FSCL_REV_TOTAL)}
              currentSort={currentSort}
              onSort={onSort}
            />
          </div>
      }
      {/* Shared: Students Total */}
      {
        tableFields.isFieldVisible(FieldName.STUDENTS_TOTAL) &&
          <div style={tableFields.getStyle(FieldName.STUDENTS_TOTAL)}>
            <SortLabel
              fieldName={FieldName.STUDENTS_TOTAL}
              isDisabled={isAllSortingDisabled ? true : !tableFields.isFieldSortable(FieldName.STUDENTS_TOTAL)}
              currentSort={currentSort}
              onSort={onSort}
            />
          </div>
      }
      {/* District-specific: Total Schools */}
      {
        tableFields.isFieldVisible(FieldName.TOTAL_SCHOOLS) &&
          <div style={tableFields.getStyle(FieldName.TOTAL_SCHOOLS)}>
            <SortLabel
              fieldName={FieldName.TOTAL_SCHOOLS}
              isDisabled={isAllSortingDisabled ? true : !tableFields.isFieldSortable(FieldName.TOTAL_SCHOOLS)}
              currentSort={currentSort}
              onSort={onSort}
            />
          </div>
      }
    </div>
  </>
}

/**
 * Unified table row component for all institution types
 * Renders appropriate columns based on tableFields configuration
 */
export function TableRowInstitution({
  row,
  tableFields
}: {
  row: any,
  tableFields: TableFields
}) {
  const [isFulltextExpanded, setIsFulltextExpanded] = useState(false)

  // Determine institution type from row data
  const type = row.type || row.institutionType;

  return <>
    <div className='results-row-content'>
      <div className="results-row-main-row">
        {/* Name column - always visible */}
        <div style={tableFields.getStyle(FieldName.NAME)}>
          {
            tableFields.isFieldVisible(FieldName.NAME) &&
              <>
                <div>
                  <Link
                    to={`/directory/institutions/${row.id}`}
                    className='main-table-link'
                    style={{ marginRight: 0 }}
                  >
                    { row['name'] }
                  </Link>
                  <div style={{ marginTop: 5 }}>
                    { [row['loc_city'] || null, General.getStateName(row['loc_state']) || null].filter(it => !!it).join(', ') }
                  </div>
                </div>
                {/* Phone/Fax - shown for District, School, Hospital */}
                {
                  (row?.phone || row?.fax) && <div style={{ marginTop: 8 }} className='desc-2'>
                    {
                      row?.phone && <span>
                        Phone { row?.phone }
                      </span>
                    }
                    {
                      (row?.phone && row?.fax) && <span>, </span>
                    }
                    {
                      row?.fax && <span>
                        Fax { row?.fax }
                      </span>
                    }
                  </div>
                }
              </>
          }
        </div>

        {/* Institution type column */}
        {
          tableFields.isFieldVisible(FieldName.TYPE) &&
            <div style={tableFields.getStyle(FieldName.TYPE)}>
              <UniversalTag category={type} />
            </div>
        }

        {/* Subtype column */}
        {
          tableFields.isFieldVisible(FieldName.SUBTYPE) &&
            <div style={tableFields.getStyle(FieldName.SUBTYPE)}>
              {row['subtype'] ? <UniversalTag category={row['subtype']} /> : <NA />}
            </div>
        }

        {/* Access column */}
        {
          tableFields.isFieldVisible(FieldName.ACCESS) &&
            <div style={tableFields.getStyle(FieldName.ACCESS)}>
              {row['access'] ? <UniversalTag category={row['access']} /> : <NA />}
            </div>
        }

        {/* School-specific: Title One */}
        {
          tableFields.isFieldVisible(FieldName.TITLE_ONE) &&
            <div style={tableFields.getStyle(FieldName.TITLE_ONE)}>
              {row['title_one'] && <UniversalTag category='TITLE_I' />}
            </div>
        }

        {/* School-specific: Grade Span */}
        {
          tableFields.isFieldVisible(FieldName.GRADE_SPAN) &&
            <div style={tableFields.getStyle(FieldName.GRADE_SPAN)}>
              <GradeSpan row={row} />
            </div>
        }

        {/* Agency-specific: State tag */}
        {
          tableFields.isFieldVisible(FieldName.LOC_STATE) &&
            <div style={tableFields.getStyle(FieldName.LOC_STATE)}>
              {
                row['loc_state']
                  ? <UniversalTag category={General.getStateName(row['loc_state'])!} />
                  : <NA />
              }
            </div>
        }

        {/* Agency-specific: Total Districts (child institutions) */}
        {
          tableFields.isFieldVisible(FieldName.TOTAL_DISTRICTS) &&
            <div style={{...tableFields.getStyle(FieldName.TOTAL_DISTRICTS), paddingRight: 5}}>
              { !row['nr_child_institutions'] ? <NA /> :
                <MetricValue
                  type='NR_INSTITUTIONS_UNDER_AGENCY'
                  value={row['nr_child_institutions']}
                  displayComponent={<span>{ General.addCommasToNumber(row['nr_child_institutions']) }</span>}
                />
              }
            </div>
        }

        {/* District/Agency: Revenue */}
        {
          tableFields.isFieldVisible(FieldName.FSCL_REV_TOTAL) &&
            <div style={{...tableFields.getStyle(FieldName.FSCL_REV_TOTAL), paddingRight: 5}}>
              { !row['revenue_total'] ? <NA /> :
                <MetricValue
                  type='INSTITUTION_ANNUAL_REVENUE'
                  value={row['revenue_total']}
                  displayComponent={<Currency amount={row['revenue_total']} />}
                />
              }
            </div>
        }

        {/* Shared: Students Total */}
        {
          tableFields.isFieldVisible(FieldName.STUDENTS_TOTAL) &&
            <div style={{...tableFields.getStyle(FieldName.STUDENTS_TOTAL), paddingRight: 5}}>
              { !row['nr_students'] ? <NA /> :
                <MetricValue
                  type='NR_STUDENTS_UNDER_INSTITUTION'
                  value={row['nr_students']}
                  displayComponent={<span>{ General.addCommasToNumber(row['nr_students']) }</span>}
                />
              }
            </div>
        }

        {/* District-specific: Total Schools (child institutions) */}
        {
          tableFields.isFieldVisible(FieldName.TOTAL_SCHOOLS) &&
            <div style={{...tableFields.getStyle(FieldName.TOTAL_SCHOOLS), paddingRight: 5}}>
              { !row['nr_child_institutions'] ? <NA /> :
                <MetricValue
                  type='NR_SCHOOLS'
                  value={row['nr_child_institutions']}
                  displayComponent={<span>{ General.addCommasToNumber(row['nr_child_institutions']) }</span>}
                />
              }
            </div>
        }

      </div>

      {/* Fulltext search matches - shown for District and School */}
      <div>
        {
          row?.fulltextMatchedUrls?.length ?
            <div style={{ marginRight: 10 }}>
              <span
                className='tiny-button'
                onClick={() => {
                  setIsFulltextExpanded(!isFulltextExpanded)
                }}
              >
                { row?.fulltextMatchedUrls?.length } URL{ row?.fulltextMatchedUrls?.length === 1 ? '' : 's' } matching fulltext search { isFulltextExpanded ? <BiChevronUp /> : <BiChevronDown /> }
              </span>
            </div> : <></>
        }
        {
          isFulltextExpanded ?
            <div style={{ marginTop: 5 }}>
              {
                row?.fulltextMatchedUrls.map((match: { url: string; snippet: string }) => (
                  <div key={match.url} style={{ fontSize: 14 }}>
                    <AmplifyLink
                      target='_blank'
                      href={ match.url }
                    >
                      { General.ellipsisInMiddle(match.url, 110) }
                    </AmplifyLink>
                    {match.snippet && (
                      <div
                        className='fulltext-snippet'
                        dangerouslySetInnerHTML={{ __html: `...${match.snippet}...` }}
                      />
                    )}
                  </div>
                ))
              }
            </div> : <></>
        }
      </div>
    </div>
  </>
}
